// export.ts — CSV export of the study library (all local; nothing leaves the device).
// Two sections in one file: saved artifacts (as plain text) + the study-session log.

import type { Artifact, StudySession, Subject } from "./store";
import { KIND_LABELS, parseArtifactBody } from "./store";

function cell(v: string | number): string {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function csv(headers: string[], rows: (string | number)[][]): string {
  return [headers, ...rows].map((r) => r.map(cell).join(",")).join("\n");
}

/** Flatten an artifact's structured body to a single plain-text cell. */
function artifactText(a: Artifact): string {
  const b = parseArtifactBody(a);
  if (!b) return "";
  if (b.kind === "notes") {
    return [
      b.notes.summary,
      ...b.notes.key_concepts.map((c) => `${c.term}: ${c.definition}`),
      ...b.notes.self_check.map((q) => `Q: ${q.question} — ${q.explanation}`),
    ].filter(Boolean).join(" | ");
  }
  if (b.kind === "explanation") {
    const x = b.explanation;
    return [x.analogy, x.definition, x.worked_example, x.misconception].filter(Boolean).join(" | ");
  }
  return [
    b.plan.goal,
    ...b.plan.weeks.map((w) => `W${w.week} ${w.label}: ${w.focus.join("; ")}${w.milestone ? ` [${w.milestone}]` : ""}`),
  ].filter(Boolean).join(" | ");
}

export function buildExport(
  artifacts: Artifact[],
  sessions: StudySession[],
  subjectById: Map<string, Subject>,
): { csv: string; filename: string } {
  const name = (id: string) => subjectById.get(id)?.name ?? "";
  const artifactsCsv = csv(
    ["Type", "Title", "Subject", "Created", "Content"],
    artifacts.map((a) => [KIND_LABELS[a.kind], a.title, name(a.subject_id), a.created_at.slice(0, 10), artifactText(a)]),
  );
  const sessionsCsv = csv(
    ["Date", "Subject", "Minutes", "Note"],
    sessions.map((s) => [s.studied_on, name(s.subject_id), s.minutes, s.note]),
  );
  const stamp = new Date().toISOString().slice(0, 10);
  return { csv: `${artifactsCsv}\n\n${sessionsCsv}`, filename: `oriora-study-${stamp}.csv` };
}
